/**
 * Guided Setup Leads — admin/superadmin list of every vendor who came
 * in through the /guided-setup path. Scans for outreach opportunities
 * (e.g. filter footfall_focused vendors to nudge them into adding a
 * conditional coupon).
 *
 * Read-only. Backed by GET /admin/intake-responses. Filter by
 * profile_tag is server-side (URL param) so a shared link stays
 * filterable across ops teammates.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Loader2, Search, Sparkles } from "lucide-react";

const TAG_META = {
  footfall_focused: {
    label: "Footfall focused",
    style: "bg-amber-500/15 text-amber-500 border-amber-500/40",
  },
  bill_value_focused: {
    label: "Bill-value focused",
    style: "bg-blue-500/15 text-blue-500 border-blue-500/40",
  },
  balanced: {
    label: "Balanced",
    style: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40",
  },
};

const PRIMARY_GOAL_LABEL = {
  new_customers:      "Attract new customers",
  repeat_customers:   "Bring customers back",
  higher_bill_value:  "Grow bill size",
};

export default function AdminIntakeLeadsPage({ superadmin = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const detailPrefix = superadmin ? "/superadmin/vendors" : "/admin/vendors";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = filter === "all" ? {} : { profile_tag: filter };
        const r = await api.adminIntakeResponses(params);
        if (!cancelled) setRows(r.data?.items || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filter]);

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => {
      const v = r.vendor || {};
      const hay = [
        v.business, v.name, v.email, v.phone, v.city,
        r.business_category,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [rows, q]);

  const counts = useMemo(() => {
    const c = { total: rows.length, footfall_focused: 0, bill_value_focused: 0, balanced: 0 };
    rows.forEach((r) => {
      const t = r.profile_tag;
      if (t && c[t] !== undefined) c[t] += 1;
    });
    return c;
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="admin-intake-leads-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Guided Setup Leads
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Vendors who came through the Guided Setup questionnaire.
            Filter by profile tag to plan targeted outreach.
          </p>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTile
          label="Total leads"
          value={counts.total}
          active={filter === "all"}
          onClick={() => setFilter("all")}
          testid="tile-all"
        />
        <SummaryTile
          label="Footfall focused"
          value={counts.footfall_focused}
          active={filter === "footfall_focused"}
          onClick={() => setFilter("footfall_focused")}
          tone="amber"
          testid="tile-footfall"
        />
        <SummaryTile
          label="Bill-value focused"
          value={counts.bill_value_focused}
          active={filter === "bill_value_focused"}
          onClick={() => setFilter("bill_value_focused")}
          tone="blue"
          testid="tile-billvalue"
        />
        <SummaryTile
          label="Balanced"
          value={counts.balanced}
          active={filter === "balanced"}
          onClick={() => setFilter("balanced")}
          tone="emerald"
          testid="tile-balanced"
        />
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by business, name, email, city…"
            className="pl-9"
            data-testid="intake-leads-search"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-52" data-testid="intake-leads-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All profile tags</SelectItem>
            <SelectItem value="footfall_focused">Footfall focused</SelectItem>
            <SelectItem value="bill_value_focused">Bill-value focused</SelectItem>
            <SelectItem value="balanced">Balanced</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading leads…
          </div>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-sm text-muted-foreground">
              No Guided Setup leads match the current filter.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="intake-leads-table">
            <thead className="bg-secondary text-muted-foreground">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">Business</th>
                <th className="px-4 py-3 font-semibold">Category</th>
                <th className="px-4 py-3 font-semibold">Primary goal</th>
                <th className="px-4 py-3 font-semibold">Profile tag</th>
                <th className="px-4 py-3 font-semibold">Submitted</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const v = r.vendor || {};
                const meta = TAG_META[r.profile_tag] || TAG_META.balanced;
                return (
                  <tr
                    key={r.id || r.vendor_id}
                    className="border-t border-border hover:bg-secondary/40"
                    data-testid={`intake-lead-row-${r.vendor_id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-foreground">
                        {v.business || "—"}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {v.name} · {v.email}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {v.city || ""} {v.phone ? `· ${v.phone}` : ""}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.business_category || "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {PRIMARY_GOAL_LABEL[r.primary_goal] || r.primary_goal || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        className={`border ${meta.style} font-bold uppercase tracking-wide`}
                      >
                        {meta.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {r.submitted_at
                        ? new Date(r.submitted_at).toLocaleString()
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Link
                        to={`${detailPrefix}/${r.vendor_id}`}
                        className="inline-flex items-center gap-1 text-primary hover:underline text-sm font-semibold"
                        data-testid={`intake-lead-open-${r.vendor_id}`}
                      >
                        Open <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

const SummaryTile = ({ label, value, active, onClick, tone, testid }) => {
  const toneCls =
    tone === "amber"   ? "hover:border-amber-500/60"
    : tone === "blue"    ? "hover:border-blue-500/60"
    : tone === "emerald" ? "hover:border-emerald-500/60"
    : "hover:border-primary/60";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-lg border bg-secondary p-3 transition-colors ${toneCls} ${
        active ? "border-primary ring-2 ring-primary/30" : "border-border"
      }`}
    >
      <p className="text-xs uppercase text-muted-foreground tracking-wider">
        {label}
      </p>
      <p className="font-heading text-2xl font-semibold mt-0.5 tabular-nums">
        {value}
      </p>
    </button>
  );
};
